"use client"

import type React from "react"

import { useState, useRef, useCallback, useEffect } from "react"

interface UseChatComposerExpansionParams {
  textareaRef: React.RefObject<HTMLTextAreaElement>
  value: string
  minLines?: number
  maxLines?: number
}

interface UseChatComposerExpansionReturn {
  isMultiLine: boolean
  computedHeight: number
}

export function useChatComposerExpansion({
  textareaRef,
  value,
  minLines = 1,
  maxLines = 6,
}: UseChatComposerExpansionParams): UseChatComposerExpansionReturn {
  const [isMultiLine, setIsMultiLine] = useState(false)
  const [computedHeight, setComputedHeight] = useState(0)
  const belowThresholdCountRef = useRef(0)
  const rafRef = useRef<number | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const recomputeHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    // Cancel any pending animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
    }

    rafRef.current = requestAnimationFrame(() => {
      // Task 3: scrollHeight-driven auto-resize (single source of truth)
      const computedStyle = getComputedStyle(textarea)
      const lineHeight = Number.parseFloat(computedStyle.lineHeight) || 24
      const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0
      const paddingBottom = Number.parseFloat(computedStyle.paddingBottom) || 0

      // Calculate min and max heights including padding
      const minHeightPx = lineHeight * minLines + paddingTop + paddingBottom
      const maxHeightPx = lineHeight * maxLines + paddingTop + paddingBottom

      // CRITICAL: Reset height to 0 or 1px to get accurate scrollHeight measurement
      textarea.style.height = "0px"
      const scrollHeight = textarea.scrollHeight

      // Clamp height between min and max
      const newHeight = Math.max(minHeightPx, Math.min(scrollHeight, maxHeightPx))

      // Apply new height in pixels
      textarea.style.height = `${newHeight}px`

      // Task 4: Set overflow mode based on whether we've hit max height
      if (scrollHeight > maxHeightPx) {
        textarea.style.overflowY = "auto"
      } else {
        textarea.style.overflowY = "hidden"
      }

      setComputedHeight(newHeight)

      // Task 5: Hysteresis with two thresholds + consecutive-collapse gating
      const EXPAND_THRESHOLD = minHeightPx + 8 // Expand at +8px
      const COLLAPSE_THRESHOLD = minHeightPx + 4 // Collapse at +4px

      if (isMultiLine) {
        // Currently multiline: check if we should collapse
        if (scrollHeight <= COLLAPSE_THRESHOLD) {
          belowThresholdCountRef.current += 1
          // Require 2 consecutive measurements below threshold to prevent oscillation
          if (belowThresholdCountRef.current >= 2) {
            setIsMultiLine(false)
            belowThresholdCountRef.current = 0
          }
        } else {
          // Reset counter if above collapse threshold
          belowThresholdCountRef.current = 0
        }
      } else {
        // Currently single-line: check if we should expand
        if (scrollHeight > EXPAND_THRESHOLD) {
          setIsMultiLine(true)
          belowThresholdCountRef.current = 0
        }
      }

      rafRef.current = null
    })
  }, [textareaRef, isMultiLine, minLines, maxLines])
  // </CHANGE>

  // Trigger recompute on value change
  useEffect(() => {
    recomputeHeight()
  }, [value, recomputeHeight])

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    resizeObserverRef.current = new ResizeObserver(() => {
      // Debounce with requestAnimationFrame to avoid layout loops
      recomputeHeight()
    })

    resizeObserverRef.current.observe(textarea)

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [textareaRef, recomputeHeight])
  // </CHANGE>

  return {
    isMultiLine,
    computedHeight,
  }
}
